import React, { useState } from 'react';
import { Send, MessageSquare, User, Mail, FileText, CheckCircle, RotateCcw } from 'lucide-react';
import { contactAPI } from '../../api/contact';
import { supabase } from '../../lib/supabase';
import { log } from '../../utils/logger';
import toast from 'react-hot-toast';

const PublicContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Check if user is authenticated
  React.useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);

      // Pre-fill form for authenticated users
      if (user) {
        setFormData(prev => ({
          ...prev,
          name: `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || '',
          email: user.email || ''
        }));
      }
    };

    checkUser();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewMessage = () => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      log.warn('Contact form validation failed - missing required fields');
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare submit data
      const submitData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim()
      };

      let result;

      if (currentUser) {
        // Authenticated user - no reCAPTCHA required, use edge function
        log.info('🔄 Submitting as authenticated user with data:', { ...submitData, message: '[REDACTED]' });

        // Edge function will automatically detect user_id from auth context
        result = await contactAPI.submit(submitData);
      } else {
        // Anonymous user - require reCAPTCHA
        log.info('🔍 Anonymous user detected - executing reCAPTCHA verification');

        // Execute reCAPTCHA verification for anonymous users
        if (!window.grecaptcha || !window.grecaptcha.enterprise) {
          throw new Error('reCAPTCHA is not available. Please refresh the page and try again.');
        }

        const token = await window.grecaptcha.enterprise.execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, {
          action: 'CONTACT_FORM'
        });

        if (!token) {
          throw new Error('reCAPTCHA verification failed. Please try again.');
        }

        log.info('reCAPTCHA token generated successfully');
        log.info('🔄 Submitting as anonymous user with data:', { ...submitData, message: '[REDACTED]' });

        // Use the secure anonymous API method with reCAPTCHA token
        result = await contactAPI.submitAnonymous({
          ...submitData,
          recaptchaToken: token
        });
      }

      const { error, success } = result;

      if (!success) {
        throw error || new Error('Failed to submit contact form');
      }

      log.info('✅ Contact form submitted successfully via secure anonymous API');

      // Show success state instead of resetting form
      setIsSubmitted(true);

      log.info('✅ Public contact form submitted successfully');
      toast.success('Thank you! Your message has been sent successfully.');
    } catch (error) {
      log.error('❌ Public contact form submission failed:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-6">
        <MessageSquare className="w-6 h-6 mr-3 text-navy-600" />
        <h3 className="text-xl font-semibold">Contact Us</h3>
      </div>

      {!isSubmitted ? (
        <>
          <p className="text-gray-600 mb-6">
            {currentUser ? (
              <>Welcome back, {currentUser.user_metadata?.first_name || 'there'}! Have questions or need support? Send us a message and we'll get back to you quickly.</>
            ) : (
              <>Have questions about TeamRezy? Want to learn how we can help your team? Send us a message and we'll get back to you quickly!</>
            )}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            <User className="w-4 h-4 inline mr-1" />
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
            placeholder="Your full name"
            required
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            <Mail className="w-4 h-4 inline mr-1" />
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
            placeholder="your.email@example.com"
            required
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
            <FileText className="w-4 h-4 inline mr-1" />
            Subject *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
            placeholder="What can we help you with?"
            required
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            <MessageSquare className="w-4 h-4 inline mr-1" />
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows="5"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
            placeholder="Tell us about your team, tournament needs, or any questions you have..."
            required
          />
        </div>


        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-navy-600 text-white px-4 py-3 rounded-md hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Sending Message...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </>
          )}
        </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              <strong>Quick Response:</strong> We typically respond within 2-4 hours during business days.
            </p>
            <p className="text-sm text-gray-600 mt-1">
              For urgent matters, call us at <a href="tel:+1-615-701-6972" className="text-navy-600 hover:underline">615-701-6972</a>
            </p>
            {!currentUser && (
              <p className="text-xs text-gray-500 mt-3">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-navy-600 hover:underline">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-navy-600 hover:underline">
                  Terms of Service
                </a>{' '}
                apply.
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h4 className="text-xl font-semibold text-gray-900 mb-2">Message Sent Successfully!</h4>
          <p className="text-gray-600 mb-6">
            {currentUser ? (
              <>Thank you, {currentUser.user_metadata?.first_name || 'there'}! We've received your message and will respond to {currentUser.email} within 2-4 hours during business days.</>
            ) : (
              <>Thank you for contacting TeamRezy. We've received your message and will respond within 2-4 hours during business days.</>
            )}
          </p>
          <div className="space-y-2 text-sm text-gray-600 mb-6">
            <p><strong>What happens next?</strong></p>
            <p>• Our team will review your message</p>
            <p>• You'll receive a personalized response via email</p>
            <p>• For urgent matters, call us at <a href="tel:+1-615-701-6972" className="text-navy-600 hover:underline">615-701-6972</a></p>
          </div>
          <button
            onClick={handleNewMessage}
            className="bg-navy-600 text-white px-6 py-2 rounded-md hover:bg-navy-800 transition-colors flex items-center mx-auto"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Send Another Message
          </button>
        </div>
      )}
    </div>
  );
};

export default PublicContactForm;